import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiRefreshCw } from 'react-icons/fi';
import { FaFileCode, FaFilePdf, FaPrint, FaLink } from 'react-icons/fa';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS, SUPABASE_STORAGE_URL } from '../Utils/ApiConfig';
import toast from 'react-hot-toast';
import SectionHeader from '../Pages/Components/SectionHeader';
import ScoreShard from '../ReportComponents/Shards/ScoreShard';
import SkillsProgressBars from '../ReportComponents/Shards/SkillsProgressBars';
import EvaluationsOverview from '../ReportComponents/Shards/EvaluationsOverview';
import TabbedReportsSection from '../ReportComponents/Shards/TabbedReportsSection';
import { getColorForSeniority } from '../Utils/ColorUtils';
import PublicLinks from '../PublicLinksComponents/PublicLinks';

/**
 * CandidateReport Component
 * Displays a comprehensive report for a candidate
 */
const CandidateReport = ({ candidate }) => {
  const navigate = useNavigate();
  const { logout, user } = useSession();
  
  // State variables
  const [isRegeneratingReport, setIsRegeneratingReport] = useState(false);
  const [isGeneratingProfileFit, setIsGeneratingProfileFit] = useState(false);
  const [isPublicLinksOpen, setIsPublicLinksOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  
  // Toggle between evaluation data and profile data
  const hasEvaluationData = !!candidate?.job_evals_report_json;
  const hasProfileData = !!candidate?.job_profile_fit_report_json;
  // Default to evaluation if available, otherwise profile
  const [reportType, setReportType] = useState(hasEvaluationData ? 'evaluation' : 'profile');

  // Function to generate job profile fit
  const handleGenerateProfileFit = async () => {
    if (!candidate?.candidate_id) {
      console.error('No candidate_id available');
      toast.error('No candidate ID available. Cannot generate job profile fit.');
      return;
    }

    if (!user?.access_token) {
      console.error('No access token available');
      toast.error('Authentication required. Please log in again.');
      return;
    }

    // Show confirmation dialog
    if (!window.confirm('Are you sure you want to generate a new job profile fit? This process takes approximately 15 minutes to complete.')) {
      return;
    }

    setIsGeneratingProfileFit(true);
    
    try {
      const response = await fetch(API_ENDPOINTS.GENERATE_JOB_PROFILE_FIT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify({
          candidate_id: candidate.candidate_id
        }),
      });

      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      const result = await response.json();
      console.log('Job profile fit generation initiated:', result);
      toast.success('Job profile fit generation has been initiated. This takes ~15 minutes - please refresh the page later to see the results.');
    } catch (error) {
      console.error('Error generating job profile fit:', error);
      
      // If it's a 401 error, it's an expired session - log out and redirect to login
      if (error.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      toast.error('Failed to generate job profile fit. Please try again.');
    } finally {
      setIsGeneratingProfileFit(false);
    }
  };

  // Function to handle regenerate report button click
  const handleRegenerateReport = async () => {
    if (!candidate?.candidate_id) {
      console.error('No candidate_id available');
      toast.error('No candidate ID available. Cannot regenerate report.');
      return;
    }

    if (!user?.access_token) {
      console.error('No access token available');
      toast.error('Authentication required. Please log in again.');
      return;
    }

    // Show confirmation dialog
    if (!window.confirm('Are you sure you want to (re)generate the candidate report?')) {
      return;
    }

    setIsRegeneratingReport(true);
    
    try {
      const response = await fetch(API_ENDPOINTS.EVALUATE_CANDIDATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify({
          candidate_id: candidate.candidate_id
        }),
      });

      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      const result = await response.json();
      console.log('Report generation initiated:', result);
      toast.success('The Evaluation is (re)generating, please refresh in a few minutes to see the update.');
    } catch (error) {
      console.error('Error generating report:', error);
      
      // If it's a 401 error, it's an expired session - log out and redirect to login
      if (error.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      toast.error('Error generating report. Please try again.');
    } finally {
      setIsRegeneratingReport(false);
    }
  };
  
  // Extract role category data - always JSON
  const roleCategory = candidate?.talent?.roleCategory || {};
  
  // Extract data from candidate if available
  const name = (candidate?.talent?.talentProfileJSON?.firstname || '') + ' ' + (candidate?.talent?.talentProfileJSON?.lastname || '') || 'N/A';
  
  // Role category and role
  const roleCategoryText = roleCategory?.mainCategory || "";
  const role = roleCategory?.subCategory || candidate?.talent?.talentProfileJSON?.role?.content || "N/A";
  const totalYearsOfExperience = candidate?.talent?.talentProfileJSON?.totalYearsOfExperience?.years || 'N/A';
  
  // Determine if we have enough data to display the report
  // We need either evaluation data OR profile data to show a meaningful report
  const hasReportData = candidate?.job_evals_report_json || candidate?.job_profile_fit_report_json;

  return (
    <div className="space-y-4">
      {/* Header section */}
      <SectionHeader
        title="Candidate Report"
        description="You can switch the Candidate Report Data to either all the evaluations completed by the candidate for this job, or a job-profile fit assessment."
        actions={hasReportData ? [
          // Public Links Button
          <button 
            key="links"
            onClick={() => setIsPublicLinksOpen(true)}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors whitespace-nowrap flex items-center"
          >
            <FaLink className="mr-2" /> Public Links
          </button>,
          
          // Print View Button
          <button 
            key="printView"
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors whitespace-nowrap flex items-center"
            onClick={() => navigate('/printable-evaluation-report', {
              state: {
                evaluationData: candidate?.job_evals_report_json,
                candidateProfile: candidate?.talent?.talentProfileJSON,
                softSkillsData: candidate?.eval_softskills_json,
                spokenLanguageData: candidate?.eval_language_json,
                cheatingData: candidate?.cheatingData,
                practicalQuizResponse: candidate?.practicalQuizResponse,
                quizDuration: candidate?.quizDuration,
                roleCategory: roleCategory,
                evals: candidate?.evals || []
              }
            })}
          >
            <FaPrint className="mr-2" /> Print View
          </button>,
          
          // Actions Dropdown
          <div key="actions" className="relative group">
            <button className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:border-gray-400 transition-colors flex items-center whitespace-nowrap">
              <span>Actions</span>
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            <div className="absolute right-0 mt-1 w-72 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              <div className="py-1">
                {/* Regenerate - Superadmin only */}
                {user?.role === 'Superadmin' && (
                  <button
                    onClick={handleRegenerateReport}
                    disabled={isRegeneratingReport || !candidate?.candidate_id}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center ${
                      isRegeneratingReport || !candidate?.candidate_id
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {isRegeneratingReport ? (
                      <>
                        <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Regenerating...
                      </>
                    ) : (
                      <>
                        <FiRefreshCw className="mr-2" />
                        Regenerate Evaluation Report
                      </>
                    )}
                  </button>
                )}
                
                {/* Generate Profile Fit - Show if profile doesn't exist OR user is Superadmin */}
                {(!hasProfileData || user?.role === 'Superadmin') && (
                  <button
                    onClick={handleGenerateProfileFit}
                    disabled={isGeneratingProfileFit || !candidate?.talent?.talentProfileJSON}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center ${
                      isGeneratingProfileFit || !candidate?.talent?.talentProfileJSON
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    title={!candidate?.talent?.talentProfileJSON ? "Candidate profile is required" : ""}
                  >
                    {isGeneratingProfileFit ? (
                      <>
                        <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        <FiRefreshCw className="mr-2" />
                        Assess Job-Profile Fit
                      </>
                    )}
                  </button>
                )}
                
                {/* Divider */}
                {(user?.role === 'Superadmin' || !hasProfileData) && (
                  <div className="border-t border-gray-200 my-1"></div>
                )}
                
                {/* Download JSON - Disabled */}
                <button
                  disabled
                  className="w-full text-left px-4 py-2 text-sm text-gray-400 cursor-not-allowed flex items-center"
                >
                  <FaFileCode className="mr-2" />
                  Download JSON
                </button>
                
                {/* Download PDF - Disabled */}
                <button
                  disabled
                  className="w-full text-left px-4 py-2 text-sm text-gray-400 cursor-not-allowed flex items-center"
                >
                  <FaFilePdf className="mr-2" />
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        ] : []}
      />
      
      {!hasReportData ? (
        <div className="bg-white shadow-md rounded-lg border border-gray-200 p-6">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="bg-blue-50 rounded-full p-6 mb-4">
              <svg className="w-16 h-16 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Report Data Available</h3>
            <p className="text-gray-600 text-center max-w-md">
              There is no evaluation data available for this candidate yet. 
              Complete evaluations will appear here once they are processed.
            </p>
          </div>
        </div>
      ) : (
        <div>
          {/* Floating Bottom Toggle - Show if we have at least profile data */}
          {hasProfileData && (
            <div 
              className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 flex items-center gap-1 bg-white rounded-full p-1 border-0 border-gray-200"
              style={{
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2)'
              }}
            >
              {/* Evaluations Data Button with Tooltip */}
              <div className="relative group">
                <button
                  onClick={() => hasEvaluationData && setReportType('evaluation')}
                  disabled={!hasEvaluationData}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    reportType === 'evaluation'
                      ? 'bg-blue-500 text-white'
                      : hasEvaluationData
                      ? 'text-gray-600 hover:text-gray-800'
                      : 'text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Evaluations Data
                </button>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
                  <div className="bg-gray-900 text-white text-sm rounded-lg py-3 px-4 w-80 shadow-lg">
                    <p className="text-center">
                      Report based on data from all completed evaluations by this candidate (for this job).
                    </p>
                    {/* Arrow */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                      <div className="border-8 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Data Button with Tooltip */}
              <div className="relative group">
                <button
                  onClick={() => setReportType('profile')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    reportType === 'profile'
                      ? 'bg-blue-500 text-white'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Profile Data
                </button>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
                  <div className="bg-gray-900 text-white text-sm rounded-lg py-3 px-4 w-80 shadow-lg">
                    <p className="text-center">
                      Report based on data from the job-profile fit assessment (ie. candidate fit to job).
                    </p>
                    {/* Arrow */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                      <div className="border-8 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Combined Candidate Info and Video Section */}
          <div className="bg-white shadow-sm rounded-lg p-6 border border-gray-200 mb-6">
              <div className="flex flex-col md:flex-row gap-10">
                {/* Left Column - Talent Information */}
                <div className="w-full md:w-2/5">
                  <div className="space-y-4">
                    {/* Talent Info */}
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">{name}</h2>
                      <p className="text-lg text-gray-700 mb-3">{role}</p>
                      
                      {/* Additional Info */}
                      <div className="space-y-2">
                        {roleCategoryText && (
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Role Category:</span> {roleCategoryText}
                          </div>
                        )}
                        
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Years of Experience:</span> {totalYearsOfExperience}
                        </div>
                        
                        {(() => {
                          const reportData = reportType === 'evaluation' 
                            ? candidate?.job_evals_report_json 
                            : candidate?.job_profile_fit_report_json;
                          
                          return (
                            <>
                              {reportData?.seniority && (
                                <div className="flex items-center text-sm">
                                  <span className="font-medium text-gray-600 mr-2">Inferred Seniority:</span>
                                  <span 
                                    className="px-2 py-1 rounded-full text-white font-medium text-xs"
                                    style={{ backgroundColor: getColorForSeniority(reportData.seniority.level) }}
                                  >
                                    {reportData.seniority.level || "N/A"}
                                  </span>
                                </div>
                              )}
                              
                              {reportData?.conclusion?.value && (
                                <div className="mt-4 pt-3 border-t border-gray-200">
                                  <p className="text-base text-gray-800 leading-relaxed">{reportData.conclusion.value}</p>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right Column - Videos */}
                <div className="w-full md:w-3/5">
                  {(() => {
                    // Parse videos from evals
                    const availableVideos = [];
                    if (candidate?.evals && Array.isArray(candidate.evals)) {
                      candidate.evals.forEach((evaluation, evalIndex) => {
                        if (evaluation.signedURL) {
                          const evaluationName = evaluation?.evaluations?.name || `Evaluation ${evalIndex + 1}`;
                          availableVideos.push({
                            url: `${SUPABASE_STORAGE_URL}${evaluation.signedURL}`,
                            evalIndex,
                            title: evaluationName,
                            evalId: evaluation.eval_id || `eval-${evalIndex + 1}`
                          });
                        }
                      });
                    }

                    // Video navigation functions
                    const goToNextVideo = () => {
                      if (currentVideoIndex < availableVideos.length - 1) {
                        setCurrentVideoIndex(currentVideoIndex + 1);
                      }
                    };

                    const goToPreviousVideo = () => {
                      if (currentVideoIndex > 0) {
                        setCurrentVideoIndex(currentVideoIndex - 1);
                      }
                    };

                    return availableVideos.length > 0 ? (
                      <div>
                        <div className="aspect-video bg-black rounded-lg overflow-hidden">
                          <video
                            key={availableVideos[currentVideoIndex]?.url}
                            controls
                            preload="metadata"
                            muted
                            playsInline
                            className="w-full h-full"
                            src={availableVideos[currentVideoIndex]?.url}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                        <div className="flex items-center mt-2 gap-4">
                          {availableVideos.length > 1 && (
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={goToPreviousVideo}
                                disabled={currentVideoIndex === 0}
                                className={`p-1 rounded ${
                                  currentVideoIndex === 0
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                                }`}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                              </button>
                              <span className="text-sm text-gray-600">
                                {currentVideoIndex + 1} of {availableVideos.length}
                              </span>
                              <button
                                onClick={goToNextVideo}
                                disabled={currentVideoIndex === availableVideos.length - 1}
                                className={`p-1 rounded ${
                                  currentVideoIndex === availableVideos.length - 1
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                                }`}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            </div>
                          )}
                          <p className="text-sm text-gray-600 font-medium">
                            {availableVideos[currentVideoIndex]?.title}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                        <div className="text-center">
                          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <p className="text-2xl font-semibold text-gray-500">No Video Available</p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Metrics Row - Only Score Shards */}
            {(() => {
              const evalData = candidate?.job_evals_report_json;
              const profileData = candidate?.job_profile_fit_report_json;
              
              const reportData = reportType === 'evaluation' ? evalData : profileData;
              const hasJobFit = reportData?.jobFit?.value;
              const hasGeneralAbility = reportData?.generalAbility?.value;
              
              return (hasJobFit || hasGeneralAbility) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Job Fit Score */}
          {hasJobFit && (
            <ScoreShard
              title="Job Fit"
              primaryValue={reportData.jobFit.value || 0}
              primaryLabel="Job Fit"
              secondaryValue={reportType === 'evaluation' ? (profileData?.jobFit?.value || 0) : null}
              secondaryLabel={reportType === 'evaluation' ? 'Job Fit (Profile)' : null}
              rationale={reportData.jobFit.rationale}
            />
          )}
                  
          {/* General Ability Score */}
          {hasGeneralAbility && (
            <ScoreShard
              title="General Ability"
              primaryValue={reportData.generalAbility.value || 0}
              primaryLabel="General Ability"
              secondaryValue={reportType === 'evaluation' ? (profileData?.generalAbility?.value || 0) : null}
              secondaryLabel={reportType === 'evaluation' ? 'General Ability (Profile)' : null}
              rationale={reportData.generalAbility.rationale}
            />
          )}
                </div>
              );
            })()}

            {/* Skills Progress Bars Section */}
            {(() => {
              const skills = reportType === 'evaluation'
                ? (candidate?.eval_hardskills_json?.hardSkills?.items || candidate?.job_evals_report_json?.hardSkills?.items || [])
                : (candidate?.job_profile_fit_report_json?.hardSkills?.items || []);
              
              return skills.length > 0 && (
                <div className="mb-6">
                  <SkillsProgressBars
                    title="Top Required Job Skills (Quick View)"
                    skills={skills}
                    showSections={true}
                  />
                </div>
              );
            })()}

            {/* Evaluations Overview Section */}
            <div className="mb-6">
              <EvaluationsOverview evals={(candidate?.evals || []).filter(evaluation => evaluation.status === 'es_completed')} />
            </div>

          {/* Tabbed Reports Section */}
          <TabbedReportsSection 
            candidate={(() => {
              const candidateObj = {
                job_profile_fit_report_json: candidate?.job_profile_fit_report_json || null,
                job_evals_report_json: candidate?.job_evals_report_json || null,
                eval_language_json: candidate?.eval_language_json || null,
                eval_softskills_json: candidate?.eval_softskills_json || null,
                eval_hardskills_json: candidate?.eval_hardskills_json || null
              };

              // Add importance from job requirements to hard skills
              candidateObj.eval_hardskills_json?.hardSkills?.items?.forEach(skill => {
                skill.importance = candidate?.jobs?.job_requirements_json?.hardSkills?.value?.find(js => js.skill === skill.skill)?.importance;
              });

              return candidateObj;
            })()} 
            talent={null} 
            evals={candidate?.evals || []}
            reportType={reportType}
          />
        </div>
      )}
      
      {/* PublicLinks Modal */}
      <PublicLinks 
        isOpen={isPublicLinksOpen} 
        onClose={() => setIsPublicLinksOpen(false)}
        entity={{
          entity_id: candidate?.candidate_id,
          entity_type: 'candidate_report'
        }}
        linkPrefix="/candidate/"
      />
    </div>
  );
};

export default CandidateReport;
